import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { CurrentUserService } from '../../core/auth/current-user.service';
import {
  CreateSaleRequest,
  CustomerProfile,
  Order,
  Product,
} from '../../core/models/product.models';
import { CustomersService } from '../../core/services/customers.service';
import { OrdersService } from '../../core/services/orders.service';
import { ProductsService } from '../../core/services/products.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';

interface InventorySaleProduct extends Product {
  purchaseItemId?: string;
  availableQuantity?: number;
  weightedAverageCost?: number;
  batchCount?: number;
  imageUrl?: string;
  maxDiscountPercent?: number;
}
interface SaleCartLine {
  product: InventorySaleProduct;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
}
type SalePaymentMethod = 'Cash' | 'UPI' | 'Card' | 'BankTransfer' | 'PayUQR' | 'Credit';
interface SalePaymentLine {
  method: SalePaymentMethod;
  amount: number;
  transactionId: string;
  referenceNumber: string;
}

@Component({
  selector: 'app-sales',
  imports: [FormsModule],
  templateUrl: './sales.html',
  styleUrl: './sales.scss',
})
export class Sales implements OnInit {
  private readonly productsService = inject(ProductsService);
  private readonly customersService = inject(CustomersService);
  private readonly ordersService = inject(OrdersService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  products: InventorySaleProduct[] = [];
  customers: CustomerProfile[] = [];
  cart: SaleCartLine[] = [];
  payments: SalePaymentLine[] = [{ method: 'Cash', amount: 0, transactionId: '', referenceNumber: '' }];
  creditDueDate = '';
  creditNotes = '';
  readonly today = new Date().toISOString().slice(0,10);
  readonly paymentMethods: Array<{ value: SalePaymentMethod; label: string }> = [
    { value: 'Cash', label: 'Cash' }, { value: 'UPI', label: 'UPI' },
    { value: 'Card', label: 'Card' }, { value: 'BankTransfer', label: 'Bank transfer' },
    { value: 'PayUQR', label: 'PayU dynamic QR' }, { value: 'Credit', label: 'Credit / due' },
  ];
  selectedCustomer: CustomerProfile | null = null;
  completedOrder: Order | null = null;
  payUQr: { qrString: string; transactionId?: string; paymentId?: string } | null = null;
  isCreatingPayUQr = false;
  search = '';
  customerPhone = '';
  selectedCustomerId = '';
  shippingAddress = '';
  billingAddress = '';
  fulfillmentType: 'Delivery' | 'Pickup' = 'Pickup';
  useShippingAsBilling = true;
  isLoadingProducts = false;
  isLookingUpCustomer = false;
  isCreatingCustomer = false;
  showCreateCustomer = false;
  newCustomer = { firstName: '', lastName: '', email: '' };
  isCompletingSale = false;
  isOpeningInvoice = false;
  errorMessage = '';
  successMessage = '';

  ngOnInit(): void {
    this.loadProducts();
    if (!this.isBusinessPortal) this.loadCustomers();
  }

  get isBusinessPortal(): boolean {
    return this.router.url.startsWith('/business');
  }

  get visibleProducts(): Product[] {
    const term = this.search.trim().toLowerCase();
    if (!term) return this.products;
    return this.products.filter((product) =>
      [product.title, product.modelName, product.city, product.area]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }

  get totalQuantity(): number {
    return this.cart.reduce((total, line) => total + line.quantity, 0);
  }

  get subtotal(): number {
    return this.cart.reduce(
      (total, line) => total + Math.max(0, Number(line.unitPrice || 0) - Number(line.discountAmount || 0)) * line.quantity,
      0,
    );
  }

  get estimatedCost(): number {
    return this.cart.reduce(
      (total, line) => total + Number(line.product.weightedAverageCost || 0) * line.quantity,
      0,
    );
  }

  get estimatedProfit(): number { return this.subtotal - this.estimatedCost; }

  get paymentTotal(): number {
    return this.payments.reduce((total, payment) => total + Number(payment.amount || 0), 0);
  }

  get paymentBalance(): number { return Math.max(0, this.subtotal - this.paymentTotal); }
  get creditAmount(): number { return this.payments.filter(payment=>payment.method==='Credit').reduce((sum,payment)=>sum+Number(payment.amount||0),0); }
  get hasCreditPayment(): boolean { return this.creditAmount>0; }

  get canCompleteSale(): boolean {
    return Boolean(this.cart.length && this.selectedCustomer && !this.isCompletingSale &&
      this.paymentTotal > 0 && Math.abs(this.paymentTotal - this.subtotal) < 0.01);
  }

  addPayment(): void {
    this.payments = [...this.payments, { method: 'UPI', amount: this.paymentBalance, transactionId: '', referenceNumber: '' }];
  }

  removePayment(index: number): void {
    if (this.payments.length === 1) return;
    this.payments = this.payments.filter((_, paymentIndex) => paymentIndex !== index);
  }

  fillPaymentBalance(index: number): void {
    const otherTotal = this.payments.reduce((total, payment, paymentIndex) =>
      paymentIndex === index ? total : total + Number(payment.amount || 0), 0);
    this.payments[index].amount = Math.max(0, Number((this.subtotal - otherTotal).toFixed(2)));
    this.payments = [...this.payments];
  }

  loadProducts(): void {
    this.isLoadingProducts = true;
    this.errorMessage = '';
    const request = this.isBusinessPortal
      ? this.ordersService.saleInventory(this.search)
      : this.productsService.list({ listingType: 'New', pageSize: 10 });

    request.pipe(finalize(() => (this.isLoadingProducts = false))).subscribe({
      next: (products: any[]) => {
        this.products = this.isBusinessPortal
          ? products.map((item) => ({
              ...item,
              id: item.productId,
              price: Number(item.suggestedSellingPrice ?? 0),
              images: item.imageUrl ? [item.imageUrl] : [],
              isReadyToSell: true,
            }))
          : products.filter((product) => this.isSellable(product));
        this.isLoadingProducts = false;
      },
      error: (error: unknown) => {
        this.isLoadingProducts = false;
        this.errorMessage = httpErrorMessage(error, 'Unable to load products for sale.');
      },
    });
  }

  addToCart(product: InventorySaleProduct): void {
    const existing = this.cart.find((line) => line.product.id === product.id);
    if (existing) {
      existing.quantity = Math.min(Number(product.availableQuantity) || 99, existing.quantity + 1);
      this.cart = [...this.cart];
      return;
    }
    this.cart = [...this.cart, { product, quantity: 1, unitPrice: Number(product.price) || 0, discountAmount: 0 }];
    this.completedOrder = null;
    this.successMessage = '';
  }

  setQuantity(line: SaleCartLine, value: number): void {
    line.quantity = Math.max(1, Math.min(Number(line.product.availableQuantity) || 99, Math.trunc(Number(value) || 1)));
    this.cart = [...this.cart];
  }

  setUnitPrice(line: SaleCartLine, value: number): void {
    line.unitPrice = Math.max(0.01, Number(value) || 0.01);
    this.cart = [...this.cart];
  }

  setDiscount(line: SaleCartLine, value: number): void {
    const maximum = this.maximumDiscount(line);
    const requested = Math.max(0, Number(value) || 0);
    if (requested > maximum) {
      this.errorMessage = `Discount for ${line.product.title} exceeds the allowed limit.`;
      window.alert('You cannot give more than the allowed discount for this product.');
      this.cart = [...this.cart];
      return;
    }
    line.discountAmount = requested;
    this.errorMessage = '';
    this.cart = [...this.cart];
  }

  maximumDiscount(line: SaleCartLine): number {
    return Number((Number(line.unitPrice || 0) * Number(line.product.maxDiscountPercent || 0) / 100).toFixed(2));
  }

  removeFromCart(productId: string): void {
    this.cart = this.cart.filter((line) => line.product.id !== productId);
  }

  lookupCustomer(): void {
    const phone = this.customerPhone.replace(/\D/g, '').slice(-10);
    if (phone.length !== 10) {
      this.errorMessage = 'Enter a valid 10-digit mobile number.';
      return;
    }

    this.isLookingUpCustomer = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.selectedCustomer = null;
    this.customersService
      .lookupByPhone(phone)
      .pipe(
        finalize(() => {
          this.isLookingUpCustomer = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (customer) => {
          this.selectedCustomer = customer;
          this.showCreateCustomer = false;
          this.successMessage = `Customer ${this.customerName(customer)} selected.`;
          this.cdr.detectChanges();
        },
        error: () => {
          this.showCreateCustomer = true;
          this.errorMessage = 'No customer found. Add this customer to continue the sale.';
          this.cdr.detectChanges();
        },
      });
  }
  customerPhoneInput(value:string):void { this.customerPhone=value.replace(/\D/g,'').slice(0,10); }

  createCustomer(): void {
    const phone = this.customerPhone.replace(/\D/g, '').slice(-10);
    if (phone.length !== 10 || !this.newCustomer.firstName.trim()) {
      this.errorMessage = 'Mobile number and first name are required.';
      return;
    }
    this.isCreatingCustomer = true;
    this.errorMessage = '';
    this.customersService
      .createForSale({ phoneNumber: phone, ...this.newCustomer })
      .pipe(
        finalize(() => {
          this.isCreatingCustomer = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (customer) => {
          this.selectedCustomer = customer;
          this.customers = [
            customer,
            ...this.customers.filter((x) => x.identityUserId !== customer.identityUserId),
          ];
          this.showCreateCustomer = false;
          this.successMessage = `Customer ${this.customerName(customer)} added and selected.`;
          this.cdr.detectChanges();
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to add customer.');
          this.cdr.detectChanges();
        },
      });
  }

  toggleCreateCustomer(): void {
    this.showCreateCustomer = !this.showCreateCustomer;
    this.errorMessage = '';
    if (!this.showCreateCustomer) this.newCustomer = { firstName: '', lastName: '', email: '' };
  }

  clearCustomer(): void {
    this.selectedCustomer = null;
    this.selectedCustomerId = '';
    this.customerPhone = '';
    this.showCreateCustomer = false;
    this.newCustomer = { firstName: '', lastName: '', email: '' };
  }

  selectCustomer(customerId: string): void {
    this.selectedCustomer =
      this.customers.find((customer) => customer.identityUserId === customerId) ?? null;
    this.errorMessage = '';
    this.successMessage = this.selectedCustomer
      ? `Customer ${this.customerName(this.selectedCustomer)} selected.`
      : '';
  }

  completeSale(): void {
    if (!this.selectedCustomer || !this.cart.length) {
      this.errorMessage =
        'Add products and select a registered customer before completing the sale.';
      return;
    }
    if (this.fulfillmentType === 'Delivery' && !this.shippingAddress.trim()) {
      this.errorMessage = 'Delivery address is required for delivery orders.';
      return;
    }
    if (Math.abs(this.paymentTotal - this.subtotal) >= 0.01) {
      this.errorMessage = `Allocate the complete payment. Remaining balance: ${this.formatAmount(this.paymentBalance)}.`;
      return;
    }
    if (this.hasCreditPayment && (!this.creditDueDate || this.creditDueDate < this.today)) {
      this.errorMessage = 'Select today or a future due date for the credit balance.';
      return;
    }

    const customerName = this.customerName(this.selectedCustomer);
    const customerPhone = this.selectedCustomer.phoneNumber || this.customerPhone;
    if (!customerPhone) {
      this.errorMessage = 'Customer phone number is required.';
      return;
    }
    const payUPayment = this.payments.find((payment) => payment.method === 'PayUQR');
    if (payUPayment && !this.selectedCustomer.email?.trim()) {
      this.errorMessage = 'Customer email is required to create a PayU QR payment.';
      return;
    }
    const fulfilmentDetails = `${this.fulfillmentType} | Customer: ${customerName} | Phone: ${customerPhone}`;
    const fulfilmentAddress =
      this.fulfillmentType === 'Delivery'
        ? `${fulfilmentDetails} | Address: ${this.shippingAddress.trim()}`
        : fulfilmentDetails;

    const payload: CreateSaleRequest = {
      buyerUserId: this.selectedCustomer.identityUserId,
      currency: 'INR',
      fulfillmentType: this.fulfillmentType,
      customerName,
      customerPhone,
      creditDueDate: this.hasCreditPayment ? this.creditDueDate : undefined,
      creditNotes: this.hasCreditPayment ? this.creditNotes.trim() || undefined : undefined,
      shippingAddress: fulfilmentAddress,
      billingAddress:
        (this.useShippingAsBilling ? fulfilmentAddress : this.billingAddress).trim() || undefined,
      items: this.cart.map(({ product, quantity, unitPrice, discountAmount }) => ({
        productId: product.id,
        purchaseItemId: product.purchaseItemId,
        sellerUserId: this.sellerUserId(product),
        productTitle: product.title,
        productImageUrl: this.productImageUrl(product) || undefined,
        productCondition: (String(product.condition).toLowerCase() === 'used' ? 'Used' : 'New') as 'New' | 'Used',
        quantity,
        unitPrice: Number((unitPrice - discountAmount).toFixed(2)),
        listUnitPrice: Number(unitPrice),
        discountAmount: Number(discountAmount),
      })),
      payments: this.payments.map((payment) => ({
        method: payment.method,
        amount: Number(payment.amount),
        transactionId: payment.transactionId.trim() || undefined,
        referenceNumber: payment.referenceNumber.trim() || undefined,
      })),
    };

    if (payload.items.some((item) => !item.sellerUserId)) {
      this.errorMessage = 'One or more products do not have a seller assigned.';
      return;
    }

    this.isCompletingSale = true;
    this.errorMessage = '';
    this.successMessage = '';
    // Open synchronously so browsers do not block the invoice tab after the API response.
    const automaticInvoiceTab = payUPayment ? null : window.open('about:blank', '_blank');
    if (automaticInvoiceTab) {
      automaticInvoiceTab.opener = null;
      automaticInvoiceTab.document.title = 'Preparing invoice…';
      automaticInvoiceTab.document.body.innerHTML =
        '<p style="font:600 16px Arial;padding:32px;color:#17324d">Preparing your ZepBazaar invoice…</p>';
    }
    this.ordersService
      .createSale(payload)
      .pipe(finalize(() => {
        this.isCompletingSale = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (order) => {
          this.completedOrder = order;
          this.cart = [];
          if (automaticInvoiceTab) {
            if (order.saleInvoice?.url) {
              automaticInvoiceTab.location.href = order.saleInvoice.url;
            } else {
              this.ordersService.saleInvoice(order.id).subscribe({
                next: (invoice) => (automaticInvoiceTab.location.href = invoice.url),
                error: () => automaticInvoiceTab.close(),
              });
            }
          }
          if (payUPayment) {
            this.createPayUQr(order, payUPayment.amount, customerName, customerPhone, this.selectedCustomer?.email || '');
          }
          this.payments = [{ method: 'Cash', amount: 0, transactionId: '', referenceNumber: '' }];
          this.creditDueDate=''; this.creditNotes='';
          const invoiceNumber = order.saleInvoice?.number ? ` ${order.saleInvoice.number}` : '';
          const emailNotice = this.selectedCustomer?.email && order.invoiceEmailSent
            ? ` Invoice${invoiceNumber} has also been sent to ${this.selectedCustomer.email}.`
            : this.selectedCustomer?.email
              ? ` Invoice${invoiceNumber} is ready to download; email delivery could not be confirmed.`
              : ` Invoice${invoiceNumber} is ready to download.`;
          this.successMessage = `Sale completed.${emailNotice}`;
          this.cdr.detectChanges();
        },
        error: (error: unknown) => {
          automaticInvoiceTab?.close();
          this.errorMessage = httpErrorMessage(
            error,
            'The sale request did not complete. Please verify the Sales list before trying again.',
          );
          this.cdr.detectChanges();
        },
      });
  }

  private createPayUQr(order: Order, amount: number, customerName: string, customerPhone: string, customerEmail: string): void {
    this.isCreatingPayUQr = true;
    this.payUQr = null;
    const callback = `${window.location.origin}${this.router.url}`;
    this.ordersService.createPayUDynamicQr({
      orderId: order.id,
      amount,
      currency: 'INR',
      customerName,
      customerEmail,
      customerPhone,
      productInfo: `ZepBazaar store sale ${order.orderNumber || order.id}`,
      successUrl: callback,
      failureUrl: callback,
      expiryMinutes: 15,
    }).pipe(finalize(() => {
      this.isCreatingPayUQr = false;
      this.cdr.detectChanges();
    })).subscribe({
      next: (response) => {
        if (!response.accepted || !response.qrString) {
          this.errorMessage = response.message || 'PayU did not create the payment QR.';
          return;
        }
        this.payUQr = { qrString: response.qrString, transactionId: response.providerReference, paymentId: response.paymentId };
        this.successMessage = 'Sale reserved. Ask the customer to complete the PayU QR payment.';
      },
      error: (error: unknown) => this.errorMessage = httpErrorMessage(error, 'Unable to create PayU QR.'),
    });
  }

  openInvoice(): void {
    if (!this.completedOrder) return;
    if (this.completedOrder.saleInvoice?.url) {
      window.open(this.completedOrder.saleInvoice.url, '_blank', 'noopener,noreferrer');
      return;
    }
    const invoiceTab = window.open('about:blank', '_blank');
    if (invoiceTab) invoiceTab.opener = null;
    this.isOpeningInvoice = true;
    this.ordersService
      .saleInvoice(this.completedOrder.id)
      .pipe(finalize(() => (this.isOpeningInvoice = false)))
      .subscribe({
        next: (invoice) => {
          if (invoiceTab) invoiceTab.location.href = invoice.url;
          else window.location.href = invoice.url;
        },
        error: (error: unknown) => {
          invoiceTab?.close();
          this.errorMessage = httpErrorMessage(error, 'Unable to open the sale invoice.');
        },
      });
  }

  printThermalInvoice(): void {
    const order = this.completedOrder;
    if (!order) return;
    const receiptWindow = window.open('', '_blank', 'width=420,height=760');
    if (!receiptWindow) {
      this.errorMessage = 'Allow pop-ups to print the thermal receipt.';
      return;
    }
    receiptWindow.opener = null;
    const paid = Math.min(Number(order.amountPaid || 0), Number(order.totalAmount || 0));
    const balance = Math.max(0, Number(order.totalAmount || 0) - paid);
    const paymentState = balance <= 0 ? 'FULLY PAID' : paid > 0 ? 'PARTIALLY PAID' : 'UNPAID';
    const sealClass = balance <= 0 ? 'paid' : paid > 0 ? 'partial' : 'unpaid';
    const customer = this.selectedCustomer ? this.escapeReceiptText(this.customerName(this.selectedCustomer)) : 'Customer';
    const phone = this.escapeReceiptText(this.selectedCustomer?.phoneNumber || this.customerPhone || '');
    const items = (order.items || []).map((item) => `
      <tr><td><b>${this.escapeReceiptText(item.productTitle)}</b><small>${item.quantity} × ${this.escapeReceiptText(this.formatAmount(item.unitPrice))}</small></td><td>${this.escapeReceiptText(this.formatAmount(item.totalPrice))}</td></tr>`).join('');
    const invoiceNumber = this.escapeReceiptText(order.saleInvoice?.number || order.orderNumber || order.id.slice(0, 8));
    const receipt = `<!doctype html><html><head><meta charset="utf-8"><title>Receipt ${invoiceNumber}</title><style>
      @page{size:80mm auto;margin:3mm}*{box-sizing:border-box}body{width:74mm;margin:0 auto;color:#111;font:12px Arial,sans-serif}header{text-align:center;border-bottom:1px dashed #555;padding:6px 0 9px}h1{font-size:19px;margin:0 0 3px}p{margin:3px 0}.meta{padding:8px 0;border-bottom:1px dashed #555}.meta div{display:flex;justify-content:space-between;gap:8px;margin:3px 0}.meta span:last-child{text-align:right}table{border-collapse:collapse;width:100%}td{padding:7px 0;border-bottom:1px dotted #aaa;vertical-align:top}td:last-child{text-align:right;white-space:nowrap}small{display:block;color:#555;margin-top:2px}.totals{padding:7px 0}.totals div{display:flex;justify-content:space-between;margin:4px 0}.totals .grand{border-top:1px solid #111;font-size:15px;font-weight:bold;padding-top:6px}.seal{align-items:center;border:3px double;border-radius:50%;display:flex;font-weight:900;height:25mm;justify-content:center;line-height:1.05;margin:9px auto;text-align:center;transform:rotate(-7deg);width:25mm}.seal.unpaid{color:#dc2626}.seal.partial{color:#7e22ce}.seal.paid{color:#16a34a}footer{text-align:center;border-top:1px dashed #555;padding-top:8px}.actions{margin-top:10px;text-align:center}.actions button{background:#087df0;border:0;border-radius:5px;color:#fff;font-weight:bold;padding:9px 16px}@media print{.actions{display:none}}
    </style></head><body><header><h1>ZepBazaar</h1><p>Store sale receipt</p></header><section class="meta"><div><span>Invoice</span><span>${invoiceNumber}</span></div><div><span>Date</span><span>${new Date(order.createdAt || Date.now()).toLocaleString('en-IN')}</span></div><div><span>Customer</span><span>${customer}</span></div>${phone ? `<div><span>Phone</span><span>${phone}</span></div>` : ''}</section><table>${items}</table><section class="totals"><div class="grand"><span>Total</span><span>${this.escapeReceiptText(this.formatAmount(order.totalAmount))}</span></div><div><span>Paid</span><span>${this.escapeReceiptText(this.formatAmount(paid))}</span></div><div><b>Balance</b><b>${this.escapeReceiptText(this.formatAmount(balance))}</b></div></section><div class="seal ${sealClass}">${paymentState.replace(' ', '<br>')}</div><footer><p>Thank you for shopping with us.</p><p>This is a computer-generated receipt.</p></footer><div class="actions"><button onclick="window.print()">Print receipt</button></div><script>window.addEventListener('load',()=>setTimeout(()=>window.print(),250));<\/script></body></html>`;
    receiptWindow.document.open();
    receiptWindow.document.write(receipt);
    receiptWindow.document.close();
  }

  private escapeReceiptText(value: unknown): string {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  customerName(customer: CustomerProfile): string {
    return `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() || 'Customer';
  }

  formatAmount(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  productImageUrl(product: Product): string {
    const image = product.images?.find(Boolean);
    if (!image) return '';
    return typeof image === 'string' ? image : image.imageUrl || image.url || image.fileUrl || '';
  }

  productLocation(product: Product): string {
    return (
      [product.area, product.city, product.state].filter(Boolean).join(', ') || 'Location not set'
    );
  }

  private loadCustomers(): void {
    this.customersService.list().subscribe({
      next: (customers) => (this.customers = customers.filter((customer) => customer.isActive)),
      error: () => (this.customers = []),
    });
  }

  private isSellable(product: Product): boolean {
    const condition =
      typeof product.condition === 'string' ? product.condition.toLowerCase() : product.condition;
    const status =
      typeof product.status === 'string' ? product.status.toLowerCase() : product.status;
    const canSellInternally =
      this.isBusinessPortal &&
      (status === 'draft' ||
        status === 'onhold' ||
        status === 'on hold' ||
        status === 'active' ||
        status === 1 ||
        status === 2 ||
        status === 6);
    const isPubliclyActive = status === 'active' || status === 2;
    return (
      (condition === 'new' || condition === 1) &&
      (canSellInternally || isPubliclyActive) &&
      product.isReadyToSell !== false &&
      Number(product.price) > 0
    );
  }

  private sellerUserId(product: Product): string {
    return product.sellerUserId || product.sellerId || this.currentUser.user()?.id || '';
  }
}
